import * as gsap from "gsap/all";
import {Callback} from "./Reflection";

export interface ITween {
    target: any;
    pause(): void;
    resume(): void;
    kill(): void;
    isActive(): boolean;
    paused(): boolean;
    progress(value?: number, suppressEvents?: boolean): boolean | ITween | any;
    time(): number;
    totalTime(): number;
}

export interface EaseFunction {
    (progress: number): number;
}

export interface Ease {
    easeIn: EaseFunction;
    easeOut: EaseFunction;
    easeInOut: EaseFunction;
}

export type TweenParams<T> = { [P in keyof T]?: T[P] } & {
    duration?: number;
    ease?: string | EaseFunction;
    delay?: number;
    repeatDelay?: number;
    immediateRender?: boolean;
    repeat?: number;
    yoyo?: boolean;
    onStart?: () => void;
    onUpdate?: () => void;
    onComplete?: () => void;
    onRepeat?: () => void;
    data?: any;
    motionPath?: { type?: "cubic", path: { [P in keyof T]?: T[P] }[], curviness?: number };
};

export const Ease = {

    backIn: gsap.Back.easeIn,
    backOut: gsap.Back.easeOut,
    backInOut: gsap.Back.easeInOut,

    bounceIn: gsap.Bounce.easeIn,
    bounceOut: gsap.Bounce.easeOut,
    bounceInOut: gsap.Bounce.easeInOut,

    circIn: gsap.Circ.easeIn,
    circOut: gsap.Circ.easeOut,
    circInOut: gsap.Circ.easeInOut,

    elasticIn: gsap.Elastic.easeIn,
    elasticOut: gsap.Elastic.easeOut,
    elasticInOut: gsap.Elastic.easeInOut,

    expoIn: gsap.Expo.easeIn,
    expoOut: gsap.Expo.easeOut,
    expoInOut: gsap.Expo.easeInOut,

    none: gsap.Linear.easeNone,

    quadIn: gsap.Power1.easeIn,
    quadOut: gsap.Power1.easeOut,
    quadInOut: gsap.Power1.easeInOut,

    cubicIn: gsap.Power2.easeIn,
    cubicOut: gsap.Power2.easeOut,
    cubicInOut: gsap.Power2.easeInOut,

    quartIn: gsap.Power3.easeIn,
    quartOut: gsap.Power3.easeOut,
    quartInOut: gsap.Power3.easeInOut,

    quintIn: gsap.Power4.easeIn,
    quintOut: gsap.Power4.easeOut,
    quintInOut: gsap.Power4.easeInOut,

    sineIn: gsap.Sine.easeIn,
    sineOut: gsap.Sine.easeOut,
    sineInOut: gsap.Sine.easeInOut,

    slowMo: gsap.SlowMo.ease,
    rough: gsap.RoughEase.ease,
};

const tween = gsap.gsap;

export const Animatable = {
    tweenTo<T extends PIXI.DisplayObject>(this: T, duration: number, ease: EaseFunction, params: TweenParams<T>) {
        params.ease = ease;
        params.duration = duration;
        return tween.to(this, params as any);
    },
    tweenFrom<T extends PIXI.DisplayObject>(this: T, duration: number, ease: EaseFunction, params: TweenParams<T>) {
        params.ease = ease;
        params.duration = duration;
        return tween.from(this, params as any);
    },
    alphaTo<T extends PIXI.DisplayObject>(this: T, alpha: number, duration: number, params?: TweenParams<T> | Callback) {
        return tween.to(this, {
            duration,
            ease: gsap.Linear.easeNone,
            alpha,
            ...(typeof params === "function" ? {onComplete: params} : params || {} as any),
        });
    },
    alphaFrom<T extends PIXI.DisplayObject>(this: T, alpha: number, duration: number, params?: TweenParams<T> | Callback) {
        return tween.from(this, {
            duration,
            ease: gsap.Linear.easeNone,
            alpha,
            ...(typeof params === "function" ? {onComplete: params} : params || {} as any),
        });
    },
    scaleTo<T extends PIXI.DisplayObject & { scaleXY: number }>(this: T, scale: number, duration: number, ease: EaseFunction, params?: TweenParams<T> | Callback) {
        return tween.to(this, {
            duration,
            ease,
            scaleXY: scale,
            ...(typeof params === "function" ? {onComplete: params} : params || {} as any),
        });
    },
    scaleFrom<T extends PIXI.DisplayObject & { scaleXY: number }>(this: T, scale: number, duration: number, ease: EaseFunction, params?: TweenParams<T> | Callback) {
        return tween.from(this, {
            duration,
            ease,
            scaleXY: scale,
            ...(typeof params === "function" ? {onComplete: params} : params || {} as any),
        });
    },
    moveXYTo<T extends PIXI.DisplayObject>(this: T, x: number, y: number, duration: number, ease: EaseFunction, params?: TweenParams<T> | Callback) {
        return tween.to(this, {
            duration,
            ease,
            x,
            y,
            ...(typeof params === "function" ? {onComplete: params} : params || {} as any),
        });
    },
    moveXYFrom<T extends PIXI.DisplayObject>(this: T, x: number, y: number, duration: number, ease: EaseFunction, params?: TweenParams<T> | Callback) {
        return tween.from(this, {
            duration,
            ease,
            x,
            y,
            ...(typeof params === "function" ? {onComplete: params} : params || {} as any),
        });
    },
    moveXTo<T extends PIXI.DisplayObject>(this: T, x: number, duration: number, ease: EaseFunction, params?: TweenParams<T> | Callback) {
        return tween.to(this, {
            duration,
            ease,
            x,
            ...(typeof params === "function" ? {onComplete: params} : params || {} as any),
        });
    },
    moveXFrom<T extends PIXI.DisplayObject>(this: T, x: number, duration: number, ease: EaseFunction, params?: TweenParams<T> | Callback) {
        return tween.from(this, {
            duration,
            ease,
            x,
            ...(typeof params === "function" ? {onComplete: params} : params || {} as any),
        });
    },
    moveYTo<T extends PIXI.DisplayObject>(this: T, y: number, duration: number, ease: EaseFunction, params?: TweenParams<T> | Callback) {
        return tween.to(this, {
            duration,
            ease,
            y,
            ...(typeof params === "function" ? {onComplete: params} : params || {} as any),
        });
    },
    moveYFrom<T extends PIXI.DisplayObject>(this: T, y: number, duration: number, ease: EaseFunction, params?: TweenParams<T> | Callback) {
        return tween.from(this, {
            duration,
            ease,
            y,
            ...(typeof params === "function" ? {onComplete: params} : params || {} as any),
        });
    },
    tintTo<T extends PIXI.DisplayObject & { tint: number }>(this: T, tint: number, duration: number, params?: TweenParams<T> | Callback) {
        (this as any).__tintAnim__ = 0;
        const fromR = (this.tint >> 16) & 0xff;
        const fromG = (this.tint >> 8) & 0xff;
        const fromB = this.tint & 0xff;
        const toR = (tint >> 16) & 0xff;
        const toG = (tint >> 8) & 0xff;
        const toB = tint & 0xff;
        return tween.to(this, duration, {
            ease: gsap.Linear.easeNone,
            __tintAnim__: 1,
            onUpdate: () => {
                const t = (this as any).__tintAnim__;
                const r = fromR + (toR - fromR) * t;
                const g = fromG + (toG - fromG) * t;
                const b = fromB + (toB - fromB) * t;
                this.tint = ((r << 16) & 0xff0000) +
                    ((g << 8) & 0x00ff00) +
                    (b & 0x0000ff);
            },
            ...(typeof params === "function" ? {onComplete: params} : params || {}),
        } as any);
    },
    tintFrom<T extends PIXI.DisplayObject & { tint: number }>(this: T, tint: number, duration: number, params?: TweenParams<T> | Callback) {
        const fromR = (tint >> 16) & 0xff;
        const fromG = (tint >> 8) & 0xff;
        const fromB = tint & 0xff;
        const toR = (this.tint >> 16) & 0xff;
        const toG = (this.tint >> 8) & 0xff;
        const toB = this.tint & 0xff;
        (this as any).__tintAnim__ = 1;
        return tween.from(this, duration, {
            ease: gsap.Linear.easeNone,
            __tintAnim__: 0,
            onUpdate: () => {
                const t = (this as any).__tintAnim__;
                const r = fromR + (toR - fromR) * t;
                const g = fromG + (toG - fromG) * t;
                const b = fromB + (toB - fromB) * t;
                this.tint = ((r << 16) & 0xff0000) +
                    ((g << 8) & 0x00ff00) +
                    (b & 0x0000ff);
            },
            ...(typeof params === "function" ? {onComplete: params} : params || {}),
        } as any);
    },
    delayedCall<T extends PIXI.DisplayObject>(this: T, time: number, cb: Callback) {
        if (time < 0 || isNaN(time)) {
            time = 0;
        }
        let i = 0;
        while (!isNaN((this as any)["-tween-delay-" + i])) {
            i++;
        }
        (this as any)["-tween-delay-" + i] = 0;
        return tween.to(this, time, {
            ease: Ease.none,
            ["-tween-delay-" + i]: time,
            onComplete: () => {
                delete (this as any)["-tween-delay-" + i];
                if (cb) {
                    cb();
                }
            },
        });
    },
    killTweens<T extends PIXI.DisplayObject>(this: T, recursive?: boolean) {
        tween.killTweensOf(this);
        if (recursive && this instanceof PIXI.Container) {
            for (const child of this.children) {
                Animatable.killTweens.call(child, true);
            }
        }
    },
};

gsap.gsap.registerPlugin(gsap.MotionPathPlugin);